import { markNotificationGroupsRead } from '../utils/markNotificationGroupsRead';
import { useCallback, useEffect, useRef, useState } from 'react';
import { notificationsApi, type AppNotification } from '../services/api';
import { mergeNotificationPages } from '../utils/mergeNotificationPages';
export function useNotificationFeed(asAthlete: boolean) {
  const [groups, setGroups] = useState<Record<string, AppNotification[]>>({});
  const [nextPage, setNextPage] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState(false);
  const revision = useRef(0);
  const busy = useRef(false);
  useEffect(() => () => { revision.current++; }, [asAthlete]);
  const loadFirst = useCallback(async () => {
    const version = ++revision.current;
    busy.current = true; setNextPage(null); setMoreError(false); setLoadingMore(false);
    try {
      const res = await notificationsApi.list(asAthlete, 1);
      if (version !== revision.current) return;
      setGroups(res.data.data.notifications ?? {});
      setNextPage(res.data.data.pagination?.next_page ?? null);
    } finally { if (version === revision.current) busy.current = false; }
  }, [asAthlete]);
  const loadMore = useCallback(async () => {
    if (busy.current || nextPage == null) return;
    busy.current = true; setLoadingMore(true); setMoreError(false);
    const version = revision.current;
    try {
      const res = await notificationsApi.list(asAthlete, nextPage);
      if (version !== revision.current) return;
      setGroups(previous => mergeNotificationPages(previous, res.data.data.notifications ?? {}));
      const next = res.data.data.pagination?.next_page;
      setNextPage(typeof next === 'number' && next > nextPage ? next : null);
    } catch { if (version === revision.current) setMoreError(true); }
    finally { if (version === revision.current) { busy.current = false; setLoadingMore(false); } }
  }, [asAthlete, nextPage]);
  const markFeedRead = useCallback(() => {
    // Discard any page fetched before the mutation so it cannot restore unread styles.
    revision.current++;
    busy.current = false;
    setLoadingMore(false);
    setMoreError(false);
    setGroups(markNotificationGroupsRead);
  }, []);
  const clearFeed = useCallback(() => {
    revision.current++;
    busy.current = false;
    setGroups({});
    setNextPage(null);
    setMoreError(false);
    setLoadingMore(false);
  }, []);
  return { groups, setGroups, clearFeed, markFeedRead, loadFirst, loadMore, loadingMore, moreError, hasMore: nextPage != null };
}
